/**
 * React Query hooks for the agent governance tabs (Approvals, Activity), backed
 * by the Squad governance API (`src/services/agentGovernance.ts`). Same token
 * mechanism as the other agent hooks ({@link useSquadAppToken}); queries stay
 * disabled until the app-token resolves.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  countWorkspacePendingApprovals,
  listApprovals,
  listAgentRuns,
  resolveApproval,
  type ApprovalAction
} from 'src/services/agentGovernance'

import { useSquadAppToken } from '../../squad'

export const governanceQueryKeys = {
  /** Prefix for every approvals query (any scope) — used to invalidate all. */
  approvalsRoot: (workspaceId: number) =>
    ['squad', 'governance', workspaceId, 'approvals'] as const,
  approvals: (workspaceId: number, agentId?: string) =>
    [
      'squad',
      'governance',
      workspaceId,
      'approvals',
      agentId ?? 'all'
    ] as const,
  runs: (workspaceId: number, agentId: string) =>
    ['squad', 'governance', workspaceId, 'runs', agentId] as const,
  pendingCount: (workspaceId: number) =>
    ['squad', 'governance', workspaceId, 'pending-approvals-count'] as const
}

/** Workspace-wide pending-approvals count for the agents-list KPI strip. */
export const useWorkspacePendingApprovals = (workspaceId: number) => {
  const appToken = useSquadAppToken(workspaceId)

  return useQuery({
    queryKey: governanceQueryKeys.pendingCount(workspaceId),
    queryFn: () => countWorkspacePendingApprovals(appToken!),
    enabled: Boolean(appToken)
  })
}

/**
 * Approvals for the tab. Pass `agentId` to filter to one agent server-side
 * (`?agent=`); pass `undefined` for the whole workspace (the "all agents"
 * toggle).
 */
export const useApprovals = (
  workspaceId: number,
  agentId: string | undefined
) => {
  const appToken = useSquadAppToken(workspaceId)

  return useQuery({
    queryKey: governanceQueryKeys.approvals(workspaceId, agentId),
    queryFn: () => listApprovals(appToken!, agentId),
    enabled: Boolean(appToken)
  })
}

export const useResolveApproval = (workspaceId: number, agentId: string) => {
  const appToken = useSquadAppToken(workspaceId)
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (vars: {
      approvalId: string
      action: ApprovalAction
      reason?: string
    }) =>
      resolveApproval(appToken!, vars.approvalId, vars.action, {
        reason: vars.reason
      }),
    onSuccess: () => {
      // Prefix match invalidates every scope (this-agent + all-agents).
      void queryClient.invalidateQueries({
        queryKey: governanceQueryKeys.approvalsRoot(workspaceId)
      })
      void queryClient.invalidateQueries({
        queryKey: governanceQueryKeys.pendingCount(workspaceId)
      })
      // Resolving resumes the run, so the activity trail gains a step.
      void queryClient.invalidateQueries({
        queryKey: governanceQueryKeys.runs(workspaceId, agentId)
      })
    }
  })
}

export const useAgentRuns = (
  workspaceId: number,
  agentId: string | undefined
) => {
  const appToken = useSquadAppToken(workspaceId)

  return useQuery({
    queryKey: governanceQueryKeys.runs(workspaceId, agentId ?? ''),
    queryFn: () => listAgentRuns(appToken!, agentId!),
    enabled: Boolean(appToken && agentId)
  })
}
